/**
 * Calendar timezone-contract helpers.
 *
 * The API stores two kinds of instants (see plan Data Design Decision #2 and
 * the `CalendarEventResponse` doc comment):
 *
 * - **Timed events** store true UTC instants. They must be rendered in the
 *   event's own IANA `timeZone` (never the viewer's), e.g. `1:00 PM EDT`.
 * - **All-day / multi-day events** store date-at-midnight UTC with an
 *   **exclusive** end. They must be formatted with `timeZone: 'UTC'`, date
 *   only, and the inclusive last day is `endsAtUtc` minus one day.
 *
 * No date library is used in this repo — these helpers wrap native
 * `Date` + `Intl.DateTimeFormat` only.
 */

import { dateTimeFormatter } from "./dateTimeParts";

import type { CalendarEventResponse } from "../types/api";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The subset of a calendar event these helpers read. */
type EventTimeFields = Pick<
  CalendarEventResponse,
  "startsAtUtc" | "endsAtUtc" | "isAllDay" | "timeZone"
>;

/**
 * Render an event's time for a compact calendar chip / band.
 *
 * - All-day events return the string `"All day"`.
 * - Timed events return the start time formatted in the event's own timezone
 *   with a short timezone-name suffix, e.g. `"1:00 PM EDT"`.
 *
 * A malformed `startsAtUtc` degrades to an empty string rather than throwing so
 * a single bad row never blanks the whole calendar.
 */
export const formatEventTime = (
  event: EventTimeFields,
  locale: string = "en-US",
): string => {
  if (event.isAllDay) {
    return "All day";
  }

  const start = new Date(event.startsAtUtc);
  if (Number.isNaN(start.getTime())) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: event.timeZone || "UTC",
      timeZoneName: "short",
    }).format(start);
  } catch {
    // An invalid IANA id (bad/legacy data) — fall back to UTC so we still show
    // a time instead of crashing the row.
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "UTC",
      timeZoneName: "short",
    }).format(start);
  }
};

/**
 * Render just the wall-clock start time of a timed event in its OWN timezone,
 * WITHOUT a timezone suffix, e.g. `"8:00 AM"`. Callers that show the tz label
 * separately (via `timeZoneAbbrev`) use this so they never have to strip a
 * locale-dependent suffix off `formatEventTime`.
 *
 * A malformed `startsAtUtc` degrades to an empty string rather than throwing.
 */
export const formatEventClockTime = (
  event: Pick<EventTimeFields, "startsAtUtc" | "timeZone">,
  locale: string = "en-US",
): string => {
  const start = new Date(event.startsAtUtc);
  if (Number.isNaN(start.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
  };
  try {
    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: event.timeZone || "UTC",
    }).format(start);
  } catch {
    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: "UTC",
    }).format(start);
  }
};

/**
 * The short timezone label for an IANA id, e.g. `"EDT"` for
 * `America/New_York` in July. This is the same `timeZoneName: "short"` label
 * `formatEventTime` appends, extracted on its own so callers that already have
 * a wall-clock time string (rather than a UTC instant) can still show the tz.
 *
 * `at` anchors the label to a date so DST is respected; it defaults to now.
 * Returns an empty string for an invalid IANA id rather than throwing.
 */
export const timeZoneAbbrev = (
  timeZone: string,
  at: Date = new Date(),
  locale: string = "en-US",
): string => {
  const tz = timeZone || "UTC";
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
};

/**
 * Format a full start→end range for the event detail / editor summary.
 *
 * - All-day single day: `"Jul 5, 2026"`.
 * - All-day multi-day:  `"Jul 5 – Jul 7, 2026"` (inclusive last day, i.e.
 *   exclusive end minus one day).
 * - Timed same day:     `"Jul 22, 2026, 8:00 AM – 6:00 PM EDT"`.
 * - Timed across days:  `"Jul 22, 2026, 8:00 AM EDT – Jul 25, 2026, 6:00 PM EDT"`.
 */
export const formatEventRange = (
  event: EventTimeFields,
  locale: string = "en-US",
): string => {
  const start = new Date(event.startsAtUtc);
  const end = new Date(event.endsAtUtc);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "";
  }

  if (event.isAllDay) {
    const dateFmt = new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
    // Exclusive end → inclusive last day.
    const inclusiveEnd = new Date(end.getTime() - MS_PER_DAY);
    const startLabel = dateFmt.format(start);
    const endLabel = dateFmt.format(inclusiveEnd);
    // Same calendar day (in UTC) → single date.
    return startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`;
  }

  const tz = event.timeZone || "UTC";
  const safeTz = (fmtOptions: Intl.DateTimeFormatOptions) => {
    try {
      return new Intl.DateTimeFormat(locale, { ...fmtOptions, timeZone: tz });
    } catch {
      return new Intl.DateTimeFormat(locale, { ...fmtOptions, timeZone: "UTC" });
    }
  };

  const dayFmt = safeTz({ month: "short", day: "numeric", year: "numeric" });
  const timeFmt = safeTz({
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

  const sameDay = dayFmt.format(start) === dayFmt.format(end);
  if (sameDay) {
    // Time-only right side; keep the tz label once at the end.
    const timeNoTz = safeTz({ hour: "numeric", minute: "2-digit" });
    return `${dayFmt.format(start)}, ${timeNoTz.format(start)} – ${timeFmt.format(end)}`;
  }
  return `${dayFmt.format(start)}, ${timeFmt.format(start)} – ${dayFmt.format(end)}, ${timeFmt.format(end)}`;
};

/**
 * Convert a stored all-day event's **exclusive** UTC end into the **inclusive**
 * end date FullCalendar and the editor's date input expect (a `YYYY-MM-DD`
 * string). Returns the last day the event actually spans.
 */
export const exclusiveEndToInclusiveDate = (endsAtUtc: string): string => {
  const end = new Date(endsAtUtc);
  const inclusive = new Date(end.getTime() - MS_PER_DAY);
  return utcDateInputValue(inclusive.toISOString());
};

/**
 * Extract the `YYYY-MM-DD` (UTC) portion of an ISO instant, for seeding a
 * `<input type="date">` from a stored all-day date-at-midnight-UTC value.
 */
export const utcDateInputValue = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

/**
 * Convert an **inclusive** all-day end date (a `YYYY-MM-DD` string, the last
 * day the event spans) into the **exclusive** UTC-midnight instant the API
 * stores. The inverse of `exclusiveEndToInclusiveDate`.
 *
 * e.g. inclusive `"2026-07-07"` -> exclusive `"2026-07-08T00:00:00.000Z"`.
 * A malformed input returns the empty string so callers can validate.
 */
export const inclusiveDateToExclusiveEndIso = (inclusiveDate: string): string => {
  const start = new Date(`${inclusiveDate}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return "";
  return new Date(start.getTime() + MS_PER_DAY).toISOString();
};

/**
 * The UTC midnight-start instant for an all-day event, from an inclusive
 * `YYYY-MM-DD` date string. Returns the empty string for bad input.
 */
export const inclusiveDateToStartIso = (date: string): string => {
  const start = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return "";
  return start.toISOString();
};

/** The browser's resolved IANA timezone, used as a sensible default for new timed events. */
export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
};

/**
 * Offset (ms) of `timeZone` at `instant`, defined as (wall-clock-as-UTC) − instant.
 * Adding it to an instant yields a Date whose UTC fields equal the tz wall clock.
 */
const tzOffsetMs = (instant: Date, timeZone: string): number => {
  try {
    // Cached: this is the hot path behind every event chip, every schedule row, and both pickers
    // on every bulk-review row.
    const parts = dateTimeFormatter("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(instant);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
    const asUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      get("hour"),
      get("minute"),
      get("second"),
    );
    return asUtc - instant.getTime();
  } catch {
    return 0;
  }
};

/**
 * Shift a true UTC instant so its UTC fields equal the event-timezone wall clock.
 * FullCalendar (configured with `timeZone="UTC"`) then buckets and displays the
 * event on the day/time it happens in its OWN timezone, not the viewer's.
 */
export const wallClockUtcIso = (iso: string, timeZone: string): string => {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return iso;
  return new Date(instant.getTime() + tzOffsetMs(instant, timeZone)).toISOString();
};

/**
 * Convert a `<input type="datetime-local">` wall-clock value (interpreted in
 * `timeZone`) into the true UTC instant to send to the API.
 */
export const zonedInputToUtcIso = (localWallClock: string, timeZone: string): string => {
  const asIfUtc = new Date(`${localWallClock}:00Z`);
  if (Number.isNaN(asIfUtc.getTime())) return new Date().toISOString();
  return new Date(asIfUtc.getTime() - tzOffsetMs(asIfUtc, timeZone)).toISOString();
};

/**
 * Convert a stored UTC instant into the `YYYY-MM-DDTHH:mm` wall-clock value (in
 * `timeZone`) that a `<input type="datetime-local">` expects when editing.
 */
export const utcToZonedInput = (iso: string, timeZone: string): string => {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return "";
  return new Date(instant.getTime() + tzOffsetMs(instant, timeZone)).toISOString().slice(0, 16);
};
